/**
 * Authentication Context
 * Manages user authentication state across the app
 * Now uses backend API instead of Supabase
 */

import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { AuthService } from '../services/authService';
import { User, AuthContextType, UserRole } from '../types';
import { message } from 'antd';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const isLoggingInRef = useRef(false);

  // Check authentication on mount
  useEffect(() => {
    let isMounted = true;
    let timeoutId: NodeJS.Timeout;
    let unsubscribe: (() => void) | null = null;
    let loadingCleared = false;

    const initAuth = async () => {
      try {
        // Check if user is authenticated
        const currentUser = await AuthService.getCurrentUser();
        
        if (isMounted) {
          setUser(currentUser);
          setIsAuthenticated(currentUser !== null);
          setIsLoading(false);
          loadingCleared = true;
        }

        // Subscribe to auth state changes
        if (isMounted) {
          unsubscribe = AuthService.onAuthStateChanged((user) => {
            if (isMounted) {
              setUser(user);
              setIsAuthenticated(user !== null);
            }
          });
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        if (isMounted) {
          setIsLoading(false);
          loadingCleared = true;
        }
      }
    };

    // Timeout fallback
    timeoutId = setTimeout(() => {
      if (isMounted && !loadingCleared) {
        console.warn('⚠️ Auth initialization timeout');
        setIsLoading(false);
        loadingCleared = true;
      }
    }, 3000);

    initAuth();

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []); // Empty dependency array is correct - only run once on mount

  const login = async (email: string, password: string) => {
    try {
      isLoggingInRef.current = true;
      setIsLoading(true);
      const user = await AuthService.signIn(email, password);
      setUser(user);
      setIsAuthenticated(true);
      message.success('Giriş başarılı!');
    } catch (error: any) {
      message.error(error.message || 'Giriş başarısız');
      throw error;
    } finally {
      isLoggingInRef.current = false;
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      await AuthService.signOut();
      setUser(null);
      setIsAuthenticated(false);
      message.success('Çıkış yapıldı');
    } catch (error: any) {
      message.error(error.message || 'Çıkış başarısız');
      throw error;
    }
  };

  const signUp = async (email: string, password: string, name: string, role: UserRole) => {
    try {
      setIsLoading(true);
      await AuthService.signUp(email, password, name, role);
      message.success('Kayıt başarılı!');
    } catch (error: any) {
      message.error(error.message || 'Kayıt başarısız');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    signUp,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
